import { FC, SyntheticEvent, useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { AccordionSummary, AccordionDetails, Box, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion } from './productAccordion.styles'
import theme from 'theme'
import {
  SSMAssetType,
  SSMCellProduct,
  SSMNistPillar,
  SSMProductStatus,
  SSMResponseProductExtended
} from 'models/SecurityStackMap'
import { getIntegrationLogoSSM } from 'lib/utils'
import ProductAccordionChip from './ProductAccordionChip'
import ProductLicenseDetails from './product-license-details'
import ProductCostDistribution from './product-cost-distribution'
import ProductOptionsMenu from './ProductOptionsMenu'
import { contractYearOptions } from '../../SSM'
import { ChipTooltip } from '../../../../components/tooltip/ChipTooltip'
import { isCurrentCell } from '../ssm.utils'

const sortByCostFn = (a: any, b: any) => (b.cost > a.cost ? -1 : 1)

const year = new Date().getFullYear()
const licenseYearOptions = [
  `${year - 2}/${year - 1}`,
  `${year - 1}/${year}`,
  `${year}/${year + 1}`, // current year
  `${year + 1}/${year + 2}`,
  `${year + 2}/${year + 3}`
]

interface Props {
  product: SSMCellProduct
  expanded?: boolean
  onChange?: (event: SyntheticEvent, isExpanded: boolean) => void
  handleEditProduct: (product: SSMCellProduct) => void
  handleOpenVendorPage: (product: SSMCellProduct) => void
  handleOpenProductPage: (product: SSMCellProduct) => void
  handleRemoveFromCell: (product: SSMCellProduct) => void
  handleRemoveFromSSM: (product: SSMCellProduct) => void
  productInstances: SSMResponseProductExtended[]
  contractYear: string
  currentNist: SSMNistPillar | null
  currentAsset: SSMAssetType | null
  alignment?: string | null
}

const ProductAccordion: FC<Props> = ({
  product,
  expanded,
  onChange,
  handleEditProduct,
  handleOpenVendorPage,
  handleOpenProductPage,
  handleRemoveFromCell,
  handleRemoveFromSSM,
  productInstances,
  contractYear,
  currentNist,
  currentAsset,
  alignment
}) => {
  const [expenseCalculationFields, setExpenseCalculationFields] = useState<any[]>([])
  const [initialSetFields, setInitialSetFields] = useState(false)
  const [isNameTruncated, setIsNameTruncated] = useState(false)
  const productNameRef = useRef<HTMLSpanElement>(null)

  // Check if text is truncated
  const checkTruncation = useCallback(() => {
    if (productNameRef.current) {
      setIsNameTruncated(productNameRef.current.scrollWidth > productNameRef.current.clientWidth)
    }
  }, [])

  // Check truncation on mount and when product name changes
  useEffect(() => {
    checkTruncation()
    window.addEventListener('resize', checkTruncation)
    return () => window.removeEventListener('resize', checkTruncation)
  }, [checkTruncation, product.display_name])

  const [selectedLicenseYear, setSelectedLicenseYear] = useState<string>(
    contractYear === 'Active Now' || contractYear === contractYearOptions[3]
      ? licenseYearOptions[2]
      : contractYear === contractYearOptions[1]
      ? licenseYearOptions[0]
      : contractYear === contractYearOptions[2]
      ? licenseYearOptions[1]
      : contractYear === contractYearOptions[4]
      ? licenseYearOptions[3]
      : licenseYearOptions[4]
  )
  const annualContractValue = String(product.cellCost)
  const vendorLogoPath = useMemo(() => getIntegrationLogoSSM(product.logo_path), [product])

  // Compute display cost based on alignment, product status, and selected year
  const displayCost = useMemo(() => {
    // For Full History view (center alignment), show different costs based on status
    if (alignment === 'center') {
      const perYear = product.license?.perYear
      if (perYear && perYear.length > 0) {
        if (product.status === SSMProductStatus.Ended) {
          // For Ended products, show last year's cost
          return perYear[perYear.length - 1].cost ?? 0
        }
        if (product.status === SSMProductStatus.Upcoming) {
          // For Upcoming products, show first year's cost
          return perYear[0].cost ?? 0
        }
      }
    }

    // Recalculate cell cost based on selected license year
    const perYear = product.license?.perYear
    if (perYear && perYear.length > 0 && currentNist && currentAsset) {
      const selectedYear = parseInt(selectedLicenseYear.split('/')[0], 10)
      const yearEntry = perYear.find((y: any) => y.year === selectedYear)
      if (yearEntry?.cost != null) {
        const currentInstance = productInstances.find((inst) =>
          isCurrentCell(inst.nist, inst.asset, currentNist, currentAsset)
        )
        const costBps = (currentInstance as any)?.costBps || 0
        if (costBps > 0) {
          return yearEntry.cost * (costBps / 10000)
        }
      }
    }

    // Default: use cellCost
    return product.cellCost ?? 0
  }, [
    alignment,
    product.status,
    product.license?.perYear,
    product.cellCost,
    selectedLicenseYear,
    productInstances,
    currentNist,
    currentAsset
  ])
  // const vendorLogoPath = useMemo(() => {
  //   const isValidLogo = (logo: string | undefined | null): boolean => {
  //     if (!logo || logo === '' || logo === 'N/A' || logo.includes('/N/A') || logo.includes('/null')) {
  //       return false
  //     }
  //     return true
  //   }
  //
  //   // If vendor exists, use vendor.logo_path; otherwise fall back to product.logo_path
  //   if (product.vendor) {
  //     const vendorLogo = product.vendor.logo_path
  //     return isValidLogo(vendorLogo) ? vendorLogo : null
  //   }
  //
  //   return isValidLogo(product.logo_path) ? product.logo_path : null
  // }, [product])

  const sumNotMatchedWarning = useMemo(() => {
    const acv = parseInt(annualContractValue, 10)
    const fieldsCost = expenseCalculationFields.reduce((acc, obj) => acc + obj.value, 0)

    if (annualContractValue === '' && fieldsCost === 0) return false
    return expenseCalculationFields.length > 1 && acv !== fieldsCost
  }, [annualContractValue, expenseCalculationFields])

  useEffect(() => {
    if (productInstances.length > 0 && !initialSetFields) {
      const expenseCalculationFieldsRes: any[] = []

      productInstances.forEach((product) => {
        expenseCalculationFieldsRes.push({
          nist: product.nist,
          asset: product.asset,
          name: product.name,
          displayName: product.display_name,
          value: product.cost
        })
      })

      setExpenseCalculationFields(expenseCalculationFieldsRes.sort(sortByCostFn))
      setInitialSetFields(true)
    }
  }, [product, initialSetFields, sumNotMatchedWarning, productInstances])

  const costDistributionItems = useMemo(() => {
    if (productInstances.length === 0) return []

    // Get the total cost for the selected license year from perYear
    const selectedYear = parseInt(selectedLicenseYear.split('/')[0], 10)
    const perYear = product.license?.perYear
    const yearEntry = perYear?.find((y: any) => y.year === selectedYear)
    const yearTotalCost = yearEntry?.cost

    return productInstances
      .map((instance) => {
        const costBps = (instance as any).costBps || 0
        const percentValue = costBps / 100

        // If we have a year-specific total cost, recalculate cell cost from costBps proportion
        // costBps is basis points (e.g., 5000 = 50%), so cell cost = yearTotalCost * (costBps / 10000)
        const instanceCost = yearTotalCost != null ? yearTotalCost * (costBps / 10000) : instance.cost || 0

        const isCurrentArea = isCurrentCell(instance.nist, instance.asset, currentNist, currentAsset)

        return {
          cell: `${instance.nist}/${instance.asset}`,
          isCurrentArea,
          percentValue: `${Math.round(percentValue)}%`,
          cost: instanceCost
        }
      })
      .sort(sortByCostFn)
  }, [productInstances, currentNist, currentAsset, selectedLicenseYear, product.license?.perYear])

  return (
    <Accordion expanded={expanded} onChange={onChange} disableGutters square>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon htmlColor={theme.baseColors.greyscale[80]} />}
        aria-controls={`product-${product.uuid}-content`}
        id={`product-${product.uuid}-header`}
      >
        {vendorLogoPath && !vendorLogoPath.includes('N/A') ? (
          <img
            className="summary-logo-image"
            src={vendorLogoPath}
            alt={product.vendor?.display_name || product.display_name || product.name}
            width="40"
            height="40"
          />
        ) : (
          <Box className="missing-vendor-logo">
            {product.vendor ? product.vendor.display_name?.[0] || 'V' : product.display_name?.[0] || 'P'}
          </Box>
        )}
        <Box className="product-name-wrapper">
          <ChipTooltip title={isNameTruncated ? product.display_name : ''} disableHoverListener={!isNameTruncated}>
            <Typography ref={productNameRef} className="product-name">
              {product.display_name}
            </Typography>
          </ChipTooltip>
          <Typography className="product-name">${displayCost.toLocaleString()}</Typography>
        </Box>
        <Box className="product-summary-space" />
        <ProductAccordionChip status={product.status} />
        <ProductOptionsMenu
          product={product}
          handleEditProduct={handleEditProduct}
          handleOpenVendorPage={handleOpenVendorPage}
          handleOpenProductPage={handleOpenProductPage}
          handleRemoveFromCell={handleRemoveFromCell}
          handleRemoveFromSSM={handleRemoveFromSSM}
        />
      </AccordionSummary>

      <AccordionDetails>
        {product.license?.note && <Typography className="description">{product.license.note}</Typography>}
        {product.license && (
          <ProductLicenseDetails
            license={product.license}
            selectedLicenseYear={selectedLicenseYear}
            setSelectedLicenseYear={setSelectedLicenseYear}
          />
        )}
        <ProductCostDistribution items={costDistributionItems} isManual={product.license?.is_auto === false} />
      </AccordionDetails>
    </Accordion>
  )
}

export default ProductAccordion
